import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { api, type Me } from '../api';
import { useIsMobile } from '../useIsMobile';
import {
  IconHome, IconDashboard, IconChat, IconCart, IconBox, IconTruck, IconAlert,
  IconSettings, IconLogout, IconSun, IconMoon, IconTag, IconMore, IconUsers,
} from './Icons';

type Entry = {
  to: string;
  label: string;
  short: string;
  title: string;
  icon: (p: { size?: number }) => JSX.Element;
  end?: boolean;
  badge?: string;
};

// Les 4 entrées du cycle de travail quotidien : ce sont elles qui occupent les
// emplacements de la barre du bas sur mobile. Tout le reste passe par « Plus ».
const ACCUEIL: Entry = { to: '/', label: 'Accueil', short: 'Accueil', title: 'Centre de contrôle', icon: IconHome, end: true };
const VENTES: Entry = { to: '/ventes', label: 'Ventes', short: 'Ventes', title: 'Ventes : devis, commandes, factures', icon: IconCart };
const ACHATS: Entry = { to: '/achats', label: 'Achats', short: 'Achats', title: 'Achats fournisseurs', icon: IconTruck };
const STOCKS: Entry = { to: '/stocks', label: 'Stocks', short: 'Stocks', title: 'Stocks', icon: IconBox };
const TABLEAUX: Entry = { to: '/tableaux', label: 'Tableaux de bord', short: 'Tableaux', title: 'Tableaux de bord', icon: IconDashboard };
const ASSISTANT: Entry = { to: '/chat', label: 'Assistant', short: 'Assistant', title: 'Assistant', icon: IconChat, badge: 'IA' };
const CLIENTS: Entry = { to: '/clients', label: 'Clients', short: 'Clients', title: 'Clients : coordonnées, langue, listes de prix', icon: IconUsers };
const LOTS: Entry = { to: '/lots', label: 'Traçabilité', short: 'Traçabilité', title: 'Traçabilité par lot (rappel produit)', icon: IconTag };
const JOURNAL: Entry = { to: '/journal', label: 'Journal', short: 'Journal', title: 'Journal technique', icon: IconAlert };
const ADMIN: Entry = { to: '/admin', label: 'Admin', short: 'Admin', title: 'Administration', icon: IconSettings };

const PRIMARY: Entry[] = [ACCUEIL, VENTES, ACHATS, STOCKS];
const SECONDARY: Entry[] = [TABLEAUX, ASSISTANT, CLIENTS, LOTS, JOURNAL];

function Item({ e, onClick }: { e: Entry; onClick?: () => void }) {
  const Ico = e.icon;
  return (
    <NavLink to={e.to} end={e.end} className="nav-item" title={e.title} onClick={onClick}>
      <span className="nav-ico"><Ico size={19} /></span>
      <span className="nav-label">{e.label}</span>
      {e.badge && <span className="nav-badge">{e.badge}</span>}
    </NavLink>
  );
}

// Global left navigation shared by every authenticated page. Dark violet
// gradient rail, grouped into Pilotage / Opérations, with a theme toggle and
// user card pinned to the bottom.
export default function NavRail({
  me,
  onLogout,
  theme,
  onToggleTheme,
}: {
  me: Me;
  onLogout: () => void;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { pathname } = useLocation();

  // La feuille « Plus » ne doit jamais survivre à un changement d'écran, sinon
  // elle masque la page qu'on vient d'ouvrir.
  useEffect(() => { setSheetOpen(false); }, [pathname]);

  async function logout() {
    await api.logout();
    onLogout();
  }

  const initial = (me.username || '?').charAt(0).toUpperCase();
  const secondary = me.is_admin ? [...SECONDARY, ADMIN] : SECONDARY;

  const themeBtn = (
    <button
      className="nav-item nav-logout"
      onClick={onToggleTheme}
      title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
    >
      <span className="nav-ico">{theme === 'dark' ? <IconSun size={19} /> : <IconMoon size={19} />}</span>
      <span className="nav-label">{theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>
    </button>
  );

  const userCard = (
    <div className="nav-user">
      <div className="nav-user-avatar">{initial}</div>
      <div className="nav-user-info">
        <div className="nav-user-name">{me.username}{me.is_admin ? ' ★' : ''}</div>
        <div className="nav-user-role">{me.is_admin ? 'Administrateur' : 'Utilisateur'}</div>
      </div>
      <button className="nav-user-logout" onClick={logout} title={`Déconnexion (${me.username})`}>
        <IconLogout size={17} />
      </button>
    </div>
  );

  // ── Mobile : 4 onglets + « Plus ». Faire tenir les 9 entrées dans 390 px
  // donnait une barre qui défilait horizontalement, donc des écrans (Achats,
  // Journal, Admin, déconnexion) invisibles sans le savoir.
  if (isMobile) {
    const onSecondary = secondary.some((e) => pathname.startsWith(e.to));
    return (
      <>
        <div
          className={`nav-sheet-backdrop ${sheetOpen ? 'open' : ''}`}
          onClick={() => setSheetOpen(false)}
        />
        <div className={`nav-sheet ${sheetOpen ? 'open' : ''}`} role="dialog" aria-label="Plus">
          <div className="nav-sheet-grip" />
          <div className="nav-sheet-items">
            {secondary.map((e) => <Item key={e.to} e={e} onClick={() => setSheetOpen(false)} />)}
          </div>
          <div className="nav-sheet-bottom">
            {themeBtn}
            {userCard}
          </div>
        </div>

        <nav className="nav-rail nav-tabbar">
          {PRIMARY.map((e) => {
            const Ico = e.icon;
            return (
              <NavLink key={e.to} to={e.to} end={e.end} className="nav-item" title={e.title}>
                <span className="nav-ico"><Ico size={20} /></span>
                <span className="nav-label">{e.short}</span>
              </NavLink>
            );
          })}
          <button
            className={`nav-item nav-more ${sheetOpen || onSecondary ? 'active' : ''}`}
            onClick={() => setSheetOpen((v) => !v)}
            aria-expanded={sheetOpen}
            title="Plus d'écrans"
          >
            <span className="nav-ico"><IconMore size={20} /></span>
            <span className="nav-label">Plus</span>
          </button>
        </nav>
      </>
    );
  }

  // ── Desktop : rail complet, inchangé.
  return (
    <nav className="nav-rail">
      <div className="nav-brand">
        <div className="nav-brand-tile">S</div>
        <div className="nav-brand-text">
          <div className="nav-brand-name">STM Partners</div>
          <div className="nav-brand-sub">Cockpit Odoo</div>
        </div>
      </div>

      <div className="nav-section">Pilotage</div>
      <div className="nav-items">
        <Item e={ACCUEIL} />
        <Item e={TABLEAUX} />
        <Item e={ASSISTANT} />
      </div>

      <div className="nav-section">Opérations</div>
      <div className="nav-items">
        <Item e={VENTES} />
        <Item e={CLIENTS} />
        <Item e={STOCKS} />
        <Item e={LOTS} />
        <Item e={ACHATS} />
        <Item e={JOURNAL} />
      </div>

      <div className="nav-bottom">
        {me.is_admin && <Item e={ADMIN} />}
        {themeBtn}
        {userCard}
      </div>
    </nav>
  );
}
